import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Flame, Check, Utensils } from 'lucide-react';
import { MEAL_TYPE_CONFIG } from './constants';

export const TodaysMealsView = ({ 
  todaysMeals, 
  recommendations, 
  advanceDay, 
  setActiveTab,
  allPlans,
  seedMealPlans,
  seeding
}) => {
  if (!todaysMeals?.has_plan) {
    return (
      <NoActivePlanView 
        recommendations={recommendations}
        setActiveTab={setActiveTab}
        allPlans={allPlans}
        seedMealPlans={seedMealPlans}
        seeding={seeding}
      />
    );
  }

  return (
    <>
      {/* Plan Progress Header */}
      <div className="bg-card border border-border p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-heading text-xl text-white uppercase tracking-wider">
              {todaysMeals.plan_name}
            </h3>
            <p className="text-muted-foreground">
              Day {todaysMeals.current_day} of {todaysMeals.total_days}
            </p>
          </div>
          <Button
            onClick={advanceDay}
            className="bg-primary text-black hover:bg-primary/90"
            data-testid="advance-day-btn"
          >
            <Check className="h-4 w-4 mr-2" />
            Complete Day
          </Button>
        </div>
        <Progress 
          value={(todaysMeals.current_day / todaysMeals.total_days) * 100} 
          className="h-3"
        />
      </div>

      {/* Macro Summary */}
      <MacroSummary todaysMeals={todaysMeals} />

      {/* Meals by Type */}
      <MealsByType todaysMeals={todaysMeals} />
    </>
  );
};

const NoActivePlanView = ({ recommendations, setActiveTab, allPlans, seedMealPlans, seeding }) => (
  <div className="bg-card border border-border p-8 text-center">
    <Utensils className="h-16 w-16 text-muted-foreground mx-auto mb-4" />
    <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-2">
      No Active Meal Plan
    </h3>
    <p className="text-muted-foreground mb-6">
      Choose a meal plan that matches your goals to get started
    </p>
    
    {/* Recommended Macros */}
    {recommendations && (
      <div className="bg-primary/10 border border-primary/30 p-6 mb-6 text-left max-w-md mx-auto">
        <h4 className="font-heading text-lg text-primary uppercase tracking-wider mb-3">
          Recommended For You
        </h4>
        <p className="text-sm text-muted-foreground mb-3">
          Based on your goal: <span className="text-white">{recommendations.user_goal?.replace('_', ' ')}</span>
        </p>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <div>
            <span className="text-muted-foreground">Daily Calories:</span>
            <span className="text-white ml-2">{recommendations.recommended_macros?.daily_calories}</span>
          </div>
          <div>
            <span className="text-muted-foreground">Protein:</span>
            <span className="text-white ml-2">{recommendations.recommended_macros?.daily_protein}g</span>
          </div>
          <div>
            <span className="text-muted-foreground">Carbs:</span>
            <span className="text-white ml-2">{recommendations.recommended_macros?.daily_carbs}g</span>
          </div>
          <div>
            <span className="text-muted-foreground">Fat:</span>
            <span className="text-white ml-2">{recommendations.recommended_macros?.daily_fat}g</span>
          </div>
        </div>
      </div>
    )}
    
    <Button
      onClick={() => setActiveTab('plans')}
      className="bg-primary text-black hover:bg-primary/90"
    >
      Browse Meal Plans
    </Button>
    
    {allPlans.length === 0 && (
      <div className="mt-4">
        <Button
          variant="outline"
          onClick={seedMealPlans}
          disabled={seeding}
        >
          {seeding ? <span className="animate-spin mr-2">⏳</span> : null}
          Generate Sample Plans
        </Button>
      </div>
    )}
  </div>
);

const MacroSummary = ({ todaysMeals }) => (
  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
    <div className="bg-card border border-border p-4 text-center">
      <Flame className="h-6 w-6 text-red-400 mx-auto mb-2" />
      <div className="text-2xl font-bold text-white">{todaysMeals.totals?.calories}</div>
      <div className="text-xs text-muted-foreground">
        / {todaysMeals.goal_macros?.calories} cal
      </div>
    </div>
    <div className="bg-card border border-border p-4 text-center">
      <div className="h-6 w-6 bg-blue-500/20 text-blue-400 mx-auto mb-2 flex items-center justify-center text-xs font-bold">P</div>
      <div className="text-2xl font-bold text-white">{todaysMeals.totals?.protein}g</div>
      <div className="text-xs text-muted-foreground">
        / {todaysMeals.goal_macros?.protein}g protein
      </div>
    </div>
    <div className="bg-card border border-border p-4 text-center">
      <div className="h-6 w-6 bg-yellow-500/20 text-yellow-400 mx-auto mb-2 flex items-center justify-center text-xs font-bold">C</div>
      <div className="text-2xl font-bold text-white">{todaysMeals.totals?.carbs}g</div>
      <div className="text-xs text-muted-foreground">
        / {todaysMeals.goal_macros?.carbs}g carbs
      </div>
    </div>
    <div className="bg-card border border-border p-4 text-center">
      <div className="h-6 w-6 bg-green-500/20 text-green-400 mx-auto mb-2 flex items-center justify-center text-xs font-bold">F</div>
      <div className="text-2xl font-bold text-white">{todaysMeals.totals?.fat}g</div>
      <div className="text-xs text-muted-foreground">
        / {todaysMeals.goal_macros?.fat}g fat
      </div>
    </div>
  </div>
);

const MealsByType = ({ todaysMeals }) => (
  <div className="space-y-6" data-testid="todays-meals">
    {['breakfast', 'lunch', 'dinner', 'snack'].map((mealType) => {
      const config = MEAL_TYPE_CONFIG[mealType];
      const Icon = config.icon;
      const meals = todaysMeals.meals_by_type?.[mealType] || [];
      
      if (meals.length === 0) return null;
      
      return (
        <div key={mealType} className="bg-card border border-border overflow-hidden">
          <div className={`${config.bg} px-4 py-3 flex items-center gap-3`}>
            <Icon className={`h-5 w-5 ${config.color}`} />
            <h4 className="font-heading text-lg text-white uppercase tracking-wider">
              {config.label}
            </h4>
          </div>
          <div className="divide-y divide-border">
            {meals.map((meal) => (
              <MealItem key={meal.id} meal={meal} />
            ))}
          </div>
        </div>
      );
    })}
  </div>
);

const MealItem = ({ meal }) => (
  <div className="p-4">
    <div className="flex items-start justify-between">
      <div>
        <h5 className="font-medium text-white">{meal.name}</h5>
        {meal.description && (
          <p className="text-muted-foreground text-sm mt-1">{meal.description}</p>
        )}
        {meal.ingredients?.length > 0 && (
          <p className="text-muted-foreground text-xs mt-2">
            {meal.ingredients.join(' • ')}
          </p>
        )}
      </div>
      <div className="text-right">
        <div className="text-lg font-bold text-primary">{meal.calories} cal</div>
        <div className="text-xs text-muted-foreground">
          P: {meal.protein}g • C: {meal.carbs}g • F: {meal.fat}g
        </div>
      </div>
    </div>
    {meal.instructions && (
      <div className="mt-3 pt-3 border-t border-border/50">
        <p className="text-sm text-white/70">{meal.instructions}</p>
      </div>
    )}
  </div>
);
